import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildJourneyDesignerFlowViewUrl,
  clearJourneyDesignerReturnContext,
  getJourneyDesignerReturnContext,
  type JourneyDesignerServiceReturnLocationState,
  setServiceUpdatedFlag
} from '../../../common/utilities/JourneyDesignerReturnContext';
import { STATUS_TYPES, STRING_CONSTANTS, URL_PATH } from '../../constants';
import { IPatchStatusModel } from '../../models';
import { AppDispatch } from '../../store/AppStore';
import usePatchRequest from '../../store/_api/services/PatchRequest';
import { getServiceNameSuccess } from '../../store/reducer/services/ServiceNameReducer';

/**
 * @method getStatusForNavigation
 * @description gets the status to update in the url
 * @param status service staus
 * @returns status
 */
const getStatusForNavigation = (status: string) => {
  switch (status?.toUpperCase()) {
    case STATUS_TYPES.APPROVED:
    case STATUS_TYPES.ACTIVE:
      return STATUS_TYPES.ACTIVE;
    case STATUS_TYPES.PENDING_APPROVAL:
      return STATUS_TYPES.PENDING_APPROVAL;
    case STATUS_TYPES.REJECTED:
    case STATUS_TYPES.RETURNED:
      return STATUS_TYPES.RETURNED;
    case STATUS_TYPES.REVIEW_CANCEL:
    case STATUS_TYPES.DRAFT:
      return STATUS_TYPES.DRAFT;
    default:
      return '';
  }
};

/**
 * @method useUpdateServiceStatus
 * @description custom hook to update the servie status
 * @param dispatchShowToast function to show toast
 * @param serviceName Servie name
 */
export const useUpdateServiceStatus = (
  success: (title: string, message: string) => void,
  failure: (title: string, message: string) => void,
  warning: (title: string, message: string) => void,
  serviceName: string,
  serviceVersion: string,
  setIsPatchUpdateCalled: (isUpdateSuccess: boolean) => void
) => {
  const { changeServiceStatus } = usePatchRequest();
  const [isServicePatchLoading, setIsServicePatchLoading] = useState(false);
  const { t: translate } = useTranslation();
  const history = useNavigate();
  const location = useLocation();
  /**
   * @method showSuccessToast
   * @description display the success toast
   * @param flow FLOW_TYPE
   */
  const showSuccessToast = (flow: string) => {
    success(
      translate('toast_component.lbl_success'),
      translate('service_wrapper_component.msg_patch_service_success_body', {
        replace: { flow }
      })
    );
  };

  const showFailureToast = (flow: string) => {
    failure(
      translate('toast_component.lbl_error'),
      translate('service_wrapper_component.msg_patch_service_fail_body', { replace: { flow } })
    );
  };

  /**
   * @method updateServiceStatus
   * @description update service status
   * @param patchStatusData IPatchStatusModel
   */

  const dispatch = useDispatch<AppDispatch>();
  const updateServiceStatus = (patchStatusData: IPatchStatusModel) => {
    setIsServicePatchLoading(true);
    changeServiceStatus(
      `${process.env.REACT_APP_API_CONTROL_PLANE_SERVICE_BASE_URL}/services/${serviceName}/version/${serviceVersion}`,
      patchStatusData
    ).then(async (serviceResponse) => {
      if (serviceResponse?.statusCode === STRING_CONSTANTS.STATUS_CODE_SUCCESS) {
        const status = getStatusForNavigation(patchStatusData.status);
        dispatch(getServiceNameSuccess(serviceResponse.serviceRecords ? [serviceResponse.serviceRecords] : []));
        showSuccessToast(translate('fsmRegistryForm.service'));
        setIsServicePatchLoading(false);
        setIsPatchUpdateCalled(false);

        const jdContext = getJourneyDesignerReturnContext();
        if (jdContext && patchStatusData.status?.toUpperCase() === STATUS_TYPES.APPROVED) {
          clearJourneyDesignerReturnContext();
          setServiceUpdatedFlag();
          const returnState: JourneyDesignerServiceReturnLocationState = {
            jdServiceReturn: true,
            jdResume: jdContext.serviceReturnResume ?? { kind: 'none' },
            jdResumeSidebar: jdContext.resumeSidebar ?? 'ALL_SERVICES',
          };
          history(buildJourneyDesignerFlowViewUrl(jdContext, 'jdReturn=1'), { state: returnState });
        } else {
          history(`${URL_PATH.SERVICE_HOME}${URL_PATH.VIEW_SERVICE}/${serviceName}/${serviceVersion}/view`, { state: { ...location.state, status } });
        }
      } else {
        setIsServicePatchLoading(false);
        showFailureToast(translate('fsmRegistryForm.service'));
      }
    }).catch(() => {
      setIsServicePatchLoading(false);
      showFailureToast(translate('fsmRegistryForm.service'));
    });
  };

  return { updateServiceStatus, isServicePatchLoading };
};
